import { getTime } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import { now } from 'utils/core/now';

interface Progress {
  progressInPercent: number;
  remainingTimeInSeconds: number;
}

export function useProgressTimer(startTime: number, duration: number): Progress {
  const [progress, setProgress] = useState<Progress>(computeProgress(startTime, duration));

  const refresh = useCallback(() => {
    setProgress(computeProgress(startTime, duration));
  }, [startTime, duration]);

  useEffect(() => {
    const dateNow = getTime(now());

    refresh();
    const intervalId = setInterval(refresh, 1000);

    const timeoutId = setTimeout(() => clearInterval(intervalId), Math.max(0, startTime + duration - dateNow));
    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  }, [startTime, duration, refresh]);

  return progress;
}

function computeProgress(startTime: number, duration: number): Progress {
  const dateNow = getTime(now());

  const endTime = startTime + duration;
  const elapsed = dateNow - startTime;
  const remainingTimeMs = endTime - dateNow;

  const remainingTimeInSeconds = Math.floor(Math.max(0, remainingTimeMs) / 1000);
  const progressInPercent = Math.max(0, Math.min(100, (elapsed * 100) / duration));

  return { remainingTimeInSeconds, progressInPercent };
}
